/**
 * ParcoursPro — page "Mon parcours pro" (Life)
 * Version .jsx sans Tailwind : styles dans ParcoursPro.css (préfixe pp-).
 * Adapté du composant ParcoursPro.tsx fourni (DA sombre SmartFeed).
 *
 * Branchement :
 *   <ParcoursPro
 *     experiences={exp} formations={edu} skills={sk}
 *     onAdd={(kind) => ...} onEdit={(kind, id) => ...} onDelete={(kind, id) => ...}
 *     onExportCv={...} onSuggestSkills={...}
 *   />
 *
 * Types de données attendus (voir mapParcoursPro.js pour la conversion
 * depuis le modèle API) :
 *   Experience { id, title, company, location?, start (ISO), end (ISO|null),
 *                contract?, via?, tags? }
 *   Formation  { id, title, school, start, end, type: Diplôme|Formation|Titre|Certification }
 *   Skill      { id, name, type, level? (1-4), suggested? }
 *
 * Toute la logique (durées, frise, stats, filtres) est en fonctions pures en tête de fichier.
 */

import React, { useMemo, useState } from 'react'
import './ParcoursPro.css'

// ───────────────────────── Logique pure ─────────────────────────

const toDate = (d, today) => (d ? new Date(d) : today)

export function monthsBetween(a, b) {
  return Math.max(0, (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth()))
}

export function formatDuration(months) {
  const y = Math.floor(months / 12)
  const m = months % 12
  if (y === 0) return `${m} mois`
  const ys = `${y} an${y > 1 ? 's' : ''}`
  return m ? `${ys} ${m} mois` : ys
}

const MONTHS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.']
export function formatPeriod(start, end) {
  const s = new Date(start)
  const f = (d) => `${MONTHS[d.getMonth()]} ${d.getFullYear()}`
  return `${f(s)} → ${end ? f(new Date(end)) : "aujourd'hui"}`
}

/** Mois d'expérience réels (union des intervalles → pas de double compte des postes parallèles). */
export function totalExperienceMonths(exps, today) {
  const iv = exps
    .map((e) => [toDate(e.start, today).getTime(), toDate(e.end, today).getTime()])
    .sort((a, b) => a[0] - b[0])
  let total = 0
  let cur = null
  for (const [s, e] of iv) {
    if (!cur || s > cur[1]) {
      if (cur) total += monthsBetween(new Date(cur[0]), new Date(cur[1]))
      cur = [s, e]
    } else cur[1] = Math.max(cur[1], e)
  }
  if (cur) total += monthsBetween(new Date(cur[0]), new Date(cur[1]))
  return total
}

/** Place des intervalles sur des pistes sans chevauchement (algorithme glouton). */
export function assignLanes(items, today) {
  const sorted = [...items].sort((a, b) => String(a.start).localeCompare(String(b.start)))
  const laneEnds = []
  return sorted.map((it) => {
    const s = toDate(it.start, today).getTime()
    const e = toDate(it.end, today).getTime()
    let lane = laneEnds.findIndex((end) => end <= s)
    if (lane === -1) {
      lane = laneEnds.length
      laneEnds.push(e)
    } else laneEnds[lane] = e
    return { ...it, lane }
  })
}

// ───────────────────────── UI atoms ─────────────────────────

const Icon = ({ d, className = 'pp-ic' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d={d} />
  </svg>
)
const I = {
  plus: 'M12 5v14M5 12h14',
  edit: 'M4 20h4l10-10-4-4L4 16v4zM13 7l4 4',
  copy: 'M8 8h12v12H8zM16 8V4H4v12h4',
  trash: 'M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13',
  search: 'M18 18l-3.5-3.5M4 11a7 7 0 1 0 14 0 7 7 0 0 0-14 0',
  download: 'M12 3v12m0 0 4-4m-4 4-4-4M4 17v3h16v-3',
  open: 'M5 12h14M13 5l7 7-7 7',
}

function RowActions({ kind, id, p }) {
  return (
    <div className="pp-rowactions">
      {p.onOpen && (
        <button className="pp-iconbtn" aria-label="Détails" title="Détails" onClick={(e) => { e.stopPropagation(); p.onOpen(kind, id) }}><Icon d={I.open} className="pp-ic-sm" /></button>
      )}
      <button className="pp-iconbtn" aria-label="Modifier" onClick={(e) => { e.stopPropagation(); p.onEdit?.(kind, id) }}><Icon d={I.edit} className="pp-ic-sm" /></button>
      <button className="pp-iconbtn" aria-label="Dupliquer" onClick={(e) => { e.stopPropagation(); p.onDuplicate?.(kind, id) }}><Icon d={I.copy} className="pp-ic-sm" /></button>
      <button className="pp-iconbtn pp-iconbtn--danger" aria-label="Supprimer" onClick={(e) => { e.stopPropagation(); p.onDelete?.(kind, id) }}><Icon d={I.trash} className="pp-ic-sm" /></button>
    </div>
  )
}

function Dot({ variant, pulse, last }) {
  return (
    <div className="pp-dotcol">
      <i className={`pp-dot pp-dot--${variant}${pulse ? ' pp-dot--pulse' : ''}`} />
      {!last && <span className="pp-dotline" />}
    </div>
  )
}

function PanelHead({ title, count, children }) {
  return (
    <div className="pp-panelhead">
      <h2 className="pp-h2">
        {title} <span className="pp-count">{count}</span>
      </h2>
      <span className="pp-spacer" />
      {children}
    </div>
  )
}

// ───────────────────────── Sections ─────────────────────────

function Stats({ experiences, formations, skills, today }) {
  const current = experiences.find((e) => e.end === null)
  const totalM = totalExperienceMonths(experiences, today)
  const firstYear = experiences.length ? Math.min(...experiences.map((e) => new Date(e.start).getFullYear())) : today.getFullYear()
  const atCompany = current ? experiences.filter((e) => e.company === current.company) : []
  const atCompanyM = totalExperienceMonths(atCompany, today)
  const certs = formations.filter((f) => f.type === 'Certification').length
  const suggested = skills.filter((s) => s.suggested).length
  const cells = [
    ['Expérience cumulée', (totalM / 12).toFixed(totalM % 12 ? 1 : 0).replace('.', ','), `ans · depuis ${firstYear}`],
    current ? [`Chez ${current.company}`, (atCompanyM / 12).toFixed(1).replace('.0', '').replace('.', ','), `ans · ${atCompany.length} poste${atCompany.length > 1 ? 's' : ''}`] : ['Poste actuel', '—', 'aucun en cours'],
    ['Formations et certifications', String(formations.length), `dont ${certs} certif${certs > 1 ? 's' : ''}`],
    ['Compétences renseignées', String(skills.filter((s) => !s.suggested).length), suggested ? `· ${suggested} suggérées par l'IA` : ''],
  ]
  return (
    <div className="pp-stats">
      {cells.map(([l, v, s]) => (
        <div key={l} className="pp-card pp-stat">
          <div className="pp-stat-label">{l}</div>
          <div className="pp-stat-value">
            {v}
            <small className="pp-stat-sub">{s}</small>
          </div>
        </div>
      ))}
    </div>
  )
}

function Timeline({ experiences, formations, today }) {
  const all = [...experiences, ...formations]
  if (!all.length) return null
  const minY = Math.min(...all.map((x) => new Date(x.start).getFullYear()))
  const maxY = today.getFullYear() + 1
  const span = maxY - minY
  const pct = (d) => {
    const dt = toDate(d, today)
    return ((dt.getFullYear() + dt.getMonth() / 12 - minY) / span) * 100
  }
  const expLanes = assignLanes(experiences, today)
  const eduLanes = assignLanes(formations, today)
  const nExp = Math.max(...expLanes.map((e) => e.lane), 0) + 1
  const years = Array.from({ length: span }, (_, i) => minY + i)

  const Bar = ({ it, cls, label }) => {
    const l = pct(it.start)
    const w = Math.max(pct(it.end) - l, 0.8)
    return (
      <div
        title={label}
        className={`pp-bar ${cls}`}
        style={{ left: `${l}%`, width: `${w}%`, top: it.lane * 34 }}
      >
        {label}
      </div>
    )
  }

  return (
    <section className="pp-card pp-timeline" aria-label="Frise chronologique">
      <div className="pp-tl-head">
        <h2 className="pp-h2">Frise {minY} → {today.getFullYear()}</h2>
        <div className="pp-legend">
          <span><i className="pp-legend-sq pp-bg-exp" />Expériences</span>
          <span><i className="pp-legend-sq pp-bg-edu" />Formations</span>
          <span><i className="pp-legend-sq pp-bg-cert" />Certifications</span>
        </div>
      </div>
      <div className="pp-tl-years" style={{ gridTemplateColumns: `repeat(${span},1fr)` }}>
        {years.map((y, i) => (
          <span key={y}>{i === 0 ? y : String(y).slice(2)}</span>
        ))}
      </div>
      <div className="pp-tl-track" style={{ height: nExp * 34 }}>
        <div className="pp-tl-now" style={{ left: `${pct(null)}%` }}>
          <span>aujourd'hui</span>
        </div>
        {expLanes.map((e) => (
          <Bar key={e.id} it={e} label={`${e.title} · ${e.company}`}
            cls={e.end === null ? 'pp-bar--exp-current' : 'pp-bar--exp'} />
        ))}
      </div>
      <div className="pp-tl-track pp-tl-track--edu" style={{ height: (Math.max(...eduLanes.map((e) => e.lane), 0) + 1) * 34 }}>
        {eduLanes.map((f) => (
          <Bar key={f.id} it={f} label={f.title}
            cls={f.type === 'Certification' ? 'pp-bar--cert' : 'pp-bar--edu'} />
        ))}
      </div>
    </section>
  )
}

const PAGE = 6

function Experiences({ p, today }) {
  const [q, setQ] = useState('')
  const [filter, setFilter] = useState(null)
  const [limit, setLimit] = useState(PAGE)
  const [sel, setSel] = useState(null)

  const sorted = useMemo(
    () => [...p.experiences].sort((a, b) => (a.end === null ? -1 : b.end === null ? 1 : String(b.start).localeCompare(String(a.start)))),
    [p.experiences],
  )
  const chips = useMemo(() => {
    const counts = new Map()
    sorted.forEach((e) => [e.company, e.location, ...(e.tags ?? [])].forEach((t) => t && counts.set(t, (counts.get(t) ?? 0) + 1)))
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6).map(([t]) => t)
  }, [sorted])
  const list = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return sorted.filter((e) => {
      const hay = [e.title, e.company, e.location, ...(e.tags ?? [])].join(' ').toLowerCase()
      return (!needle || hay.includes(needle)) && (!filter || hay.includes(filter.toLowerCase()))
    })
  }, [sorted, q, filter])
  const shown = list.slice(0, limit)

  return (
    <section className="pp-card pp-panel" aria-label="Expériences">
      <PanelHead title="Expériences" count={list.length}>
        <label className="pp-search">
          <Icon d={I.search} className="pp-ic-sm" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Poste, entreprise, compétence…" />
        </label>
      </PanelHead>
      <div className="pp-chips">
        {[null, ...chips].map((c) => (
          <button key={c ?? 'all'} onClick={() => setFilter(c)}
            className={`pp-chip${filter === c ? ' pp-chip--active' : ''}`}>
            {c ?? 'Toutes'}
          </button>
        ))}
      </div>

      {shown.length === 0 && (
        <div className="pp-empty">
          <p className="pp-empty-title">Aucune expérience ne correspond</p>
          <p>Modifie la recherche ou <button className="pp-linkbtn" onClick={() => p.onAdd?.('experience')}>ajoute une expérience</button>.</p>
        </div>
      )}

      {shown.map((e, i) => {
        const current = e.end === null
        const months = monthsBetween(new Date(e.start), toDate(e.end, today))
        return (
          <div key={e.id} onClick={() => setSel(e.id)}
            className={`pp-row${sel === e.id ? ' pp-row--sel' : ''}`}>
            <Dot variant={current ? 'exp' : 'exp-past'} pulse={current} last={i === shown.length - 1} />
            <div>
              <div className="pp-row-title">{e.title}</div>
              <div className="pp-text2 pp-row-sub">
                {e.company}{e.location && ` · ${e.location}`}
                {e.via && <span className="pp-text3"> · en mission via {e.via}</span>}
              </div>
              <div className="pp-row-meta pp-mono pp-text3">
                <span>{formatPeriod(e.start, e.end)}</span>
                {e.contract && <span className="pp-badge">{e.contract}</span>}
              </div>
              {!!e.tags?.length && (
                <div className="pp-tags">
                  {e.tags.map((t) => <span key={t} className="pp-tag">{t}</span>)}
                </div>
              )}
            </div>
            <div className="pp-row-right pp-mono pp-text2">
              {current ? <span className="pp-current">en cours</span> : formatDuration(months)}
            </div>
            <RowActions kind="experience" id={e.id} p={p} />
          </div>
        )
      })}

      {list.length > limit && (
        <button onClick={() => setLimit((l) => l + PAGE * 2)} className="pp-more">
          Afficher les {list.length - limit} expériences restantes ↓
        </button>
      )}
    </section>
  )
}

function Formations({ p, today }) {
  const [limit, setLimit] = useState(5)
  const sorted = useMemo(() => [...p.formations].sort((a, b) => String(b.start).localeCompare(String(a.start))), [p.formations])
  const shown = sorted.slice(0, limit)
  return (
    <section className="pp-card pp-panel" aria-label="Formations">
      <PanelHead title="Formations et certifications" count={sorted.length}>
        <button className="pp-btn pp-btn--sm" onClick={() => p.onAdd?.('formation')}>+ Ajouter</button>
      </PanelHead>
      {shown.map((f, i) => {
        const cert = f.type === 'Certification'
        return (
          <div key={f.id} className="pp-row pp-row--static">
            <Dot variant={cert ? 'cert' : 'edu'} last={i === shown.length - 1} />
            <div>
              <div className="pp-row-title">{f.title}</div>
              <div className="pp-text2 pp-row-sub">{f.school}</div>
              <div className="pp-row-meta pp-mono pp-text3">
                <span>{formatPeriod(f.start, f.end)}</span>
                <span className="pp-badge">{f.type}</span>
              </div>
            </div>
            <div className={`pp-row-right pp-mono ${cert ? 'pp-cert' : 'pp-text2'}`}>
              {cert ? 'valide' : formatDuration(monthsBetween(new Date(f.start), toDate(f.end, today)))}
            </div>
            <RowActions kind="formation" id={f.id} p={p} />
          </div>
        )
      })}
      {sorted.length > limit && (
        <button onClick={() => setLimit(sorted.length)} className="pp-more">
          Afficher les {sorted.length - limit} restantes ↓
        </button>
      )}
    </section>
  )
}

// Types alignés sur le backend (Professional.Competence.COMPETENCE_TYPES)
const TYPE_COLOR = { 'Métier': 'pp-bg-exp', 'Interperso': 'pp-bg-edu', 'Linguistique': 'pp-bg-cert' }
const SKILL_GROUPS = ['Métier', 'Interperso', 'Linguistique']

function Skills({ p }) {
  const groups = useMemo(() => {
    const m = new Map()
    p.skills.forEach((s) => m.set(s.type, [...(m.get(s.type) ?? []), s]))
    SKILL_GROUPS.forEach((t) => !m.has(t) && m.set(t, []))
    return [...m.entries()]
  }, [p.skills])
  const validated = p.skills.filter((s) => !s.suggested).length
  return (
    <section className="pp-card pp-panel" aria-label="Compétences">
      <PanelHead title="Compétences" count={validated}>
        <button className="pp-btn pp-btn--sm" onClick={p.onSuggestSkills}>✦ Suggérer depuis mes expériences</button>
      </PanelHead>
      <div className="pp-skills">
        {groups.map(([type, items]) => {
          const suggested = items.some((s) => s.suggested)
          return (
            <div key={type} className="pp-skillgroup">
              <div className="pp-skillgroup-label">
                <i className={`pp-legend-sq ${TYPE_COLOR[type] ?? 'pp-bg-neutral'}`} />
                {type}
                {suggested && <span className="pp-skillgroup-note">· suggérées, à valider</span>}
              </div>
              <div className="pp-skilllist">
                {items.map((s) => (
                  <button key={s.id}
                    onClick={() => (s.suggested ? p.onAcceptSkill?.(s.id) : p.onEdit?.('skill', s.id))}
                    title={s.suggested ? 'Cliquer pour valider' : 'Modifier'}
                    className={`pp-skill${s.suggested ? ' pp-skill--suggested' : ''}`}>
                    {s.name}
                    {s.level && !s.suggested && (
                      <span className="pp-lvl" aria-label={`niveau ${s.level}/4`}>
                        {[1, 2, 3, 4].map((n) => <b key={n} className={n <= s.level ? 'pp-lvl--on' : ''} />)}
                      </span>
                    )}
                  </button>
                ))}
                <button onClick={() => p.onAdd?.('skill')} className="pp-skill pp-skill--add">+ Ajouter</button>
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}

// ───────────────────────── Page ─────────────────────────

export default function ParcoursPro(props) {
  const today = props.today ?? new Date()
  const current = props.experiences.find((e) => e.end === null)
  return (
    <div className="pp">
      <main className="pp-main">
        <div className="pp-header">
          <div>
            <h1 className="pp-h1">Mon parcours pro</h1>
            <div className="pp-text2 pp-header-sub">
              {current ? (
                <>Actuellement <b>{current.title}</b> chez <b>{current.company}</b>{current.location && `, ${current.location}`} · depuis {MONTHS[new Date(current.start).getMonth()]} {new Date(current.start).getFullYear()}</>
              ) : 'Aucun poste en cours'}
            </div>
          </div>
          <div className="pp-header-actions">
            <button className="pp-btn" onClick={props.onExportCv}><Icon d={I.download} />Exporter CV</button>
            <button className="pp-btn pp-btn--primary" onClick={() => props.onAdd?.('experience')}><Icon d={I.plus} />Ajouter</button>
          </div>
        </div>

        <Stats experiences={props.experiences} formations={props.formations} skills={props.skills} today={today} />
        <Timeline experiences={props.experiences} formations={props.formations} today={today} />

        <div className="pp-cols">
          <Experiences p={props} today={today} />
          <div className="pp-col-right">
            <Formations p={props} today={today} />
            <Skills p={props} />
          </div>
        </div>
      </main>
    </div>
  )
}
